import React, { useEffect } from 'react'
import OCRScopingJobsViewModel from './OCRScopingJobsViewModel'
import { useDocumentTitle } from '../../../hooks/document-title'
import styles from './OCRScopingJobsContent.sass'
import { observer } from 'mobx-react'
import {
  SplitView,
  SplitViewMain,
  SplitViewSide,
  SplitViewToggleButton,
} from 'components/SplitView'
import Icon from 'components/Icon'
import { useHoverState } from 'hooks/hover-notify'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { useActivation } from 'hooks/mobx-hooks'
import OCRScopingJobsSidePanel from './components/SidePanel/index'
import OCRScopingJobMainPanel from './components/MainPanel'
import CreateOCRScopeJobsDialog, {
  CreateOCRScopeJobsDraggingOverlay,
} from './views/CreateOCRScopeJobsDialog'
import { useDropzone } from 'hooks/use-dropzone'
import { RouteChildrenProps } from 'react-router'

interface OCRScopingJobsProps
  extends RouteChildrenProps<{ scopeWorkId?: string }> {
  vm: OCRScopingJobsViewModel
}

export default observer(function OCRScopingJobs({
  vm,
  match,
  ...rest
}: OCRScopingJobsProps) {
  const id = match?.params?.scopeWorkId
  useDocumentTitle('Scoping Jobs')
  useActivation(vm)

  useEffect(() => {
    vm.ocrScopingJobMainPanelViewModel.activate(id)
  }, [id])
  const isDoneFetchingAggregations =
    vm.ocrScopingJobSidePanelViewModel.isDoneFetchingAggregations

  return vm.activate.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => {
      if (!isDoneFetchingAggregations) {
        return <ContentLoading />
      }
      return <OCRScopingJobsContent vm={vm} match={match} {...rest} />
    },
  })
})

const OCRScopingJobsContent = observer(function OCRScopingJobsContent({
  vm,
}: OCRScopingJobsProps) {
  const [highlightBorder, setHighlightBorder] = React.useState(false)
  const toggleRef = React.useRef<HTMLAnchorElement | null>(null)
  const isToggleHovering = useHoverState(toggleRef, setHighlightBorder)

  const createOCRScopeJobsVM = vm.createOCRScopeJobsViewModel
  function onDrop(files: FileList) {
    createOCRScopeJobsVM.addFiles(files)
  }

  function onEnter() {
    createOCRScopeJobsVM.setDragging()
  }

  function onLeave() {
    createOCRScopeJobsVM.stopDragging()
  }
  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  if (
    createOCRScopeJobsVM.selectedFiles.length > 0 &&
    !createOCRScopeJobsVM.showing
  ) {
    createOCRScopeJobsVM.open()
  }

  const ocrScopingJobSidePanelVM = vm.ocrScopingJobSidePanelViewModel
  return (
    <div className={styles.container} ref={dropZoneRef}>
      {createOCRScopeJobsVM.dragging && <CreateOCRScopeJobsDraggingOverlay />}
      <SplitView>
        <SplitViewSide
          collapsed={!vm.sidePanelVisible}
          highlightBorder={highlightBorder}
        >
          <OCRScopingJobsSidePanel
            vm={ocrScopingJobSidePanelVM}
            createOCRScopeJobsVM={createOCRScopeJobsVM}
            ocrScopingJobMainPanelVM={vm.ocrScopingJobMainPanelViewModel}
          />
        </SplitViewSide>

        <SplitViewMain>
          <div className={styles.toggleButtonContainer}>
            <SplitViewToggleButtonContainer
              onNavSidebarToggleClick={vm.toggleSidePanelVisibleVisibility}
              isNavSidebarVisible={vm.sidePanelVisible}
              isToggleHovering={isToggleHovering}
            />
          </div>
          <OCRScopingJobMainPanel vm={vm} />
        </SplitViewMain>
      </SplitView>
      <CreateOCRScopeJobsDialog vm={createOCRScopeJobsVM} />
    </div>
  )
})

interface SplitViewToggleButtonContainerProps {
  onNavSidebarToggleClick?: () => unknown
  isNavSidebarVisible: boolean
  isToggleHovering: boolean
  toggleRef?: React.Ref<HTMLAnchorElement>
}

export const SplitViewToggleButtonContainer = observer(
  ({
    onNavSidebarToggleClick,
    isNavSidebarVisible,
    isToggleHovering,
    toggleRef,
  }: SplitViewToggleButtonContainerProps) => {
    if (onNavSidebarToggleClick) {
      return (
        <SplitViewToggleButton
          onClick={onNavSidebarToggleClick}
          ref={toggleRef}
        >
          {isToggleHovering ? (
            isNavSidebarVisible ? (
              <Icon name="hover-arrow-highlight" />
            ) : (
              <Icon name="hover-menu-highlight" />
            )
          ) : isNavSidebarVisible ? (
            <Icon name="hover-arrow" />
          ) : (
            <Icon name="hover-menu" />
          )}
        </SplitViewToggleButton>
      )
    }
    return null
  },
)
